import React, { useState } from 'react';
import { Home, Pencil, Plus, X, Loader2, MapPin } from 'lucide-react';
import Modal from './Modal';
import { safeFetch } from '../../lib/api';
import { normalizePlacements, HOUSING_RENT_PERIODS, type HousingArrangement, type PlacementEntry } from '../../lib/placementUtils';

export interface HousingArrangementEditorProps {
  applicationId: string;
  /** A normalised placement entry — its `housing` sub-object is read/edited. */
  entry: PlacementEntry;
  /** When true the viewer may add/edit the housing arrangement in place. */
  canEdit: boolean;
  /** Refresh the parent's source-of-truth with the server-returned array. */
  onUpdated?: (placements: PlacementEntry[]) => void;
  /** Optional helper line shown under the title (e.g. a lock explanation). */
  hint?: string;
  /** Compact styling for dense admin panels (defaults to the applicant view). */
  dense?: boolean;
}

const fieldGrid = (dense: boolean, childCount: number) =>
  dense
    ? 'grid grid-cols-2 gap-x-4 gap-y-1.5 sm:grid-cols-3'
    : `grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-3 ${childCount % 2 === 1 ? 'lg:grid-cols-3' : ''}`;

export default function HousingArrangementEditor({
  applicationId,
  entry,
  canEdit,
  onUpdated,
  hint,
  dense = false,
}: HousingArrangementEditorProps) {
  const housing = entry.housing || undefined;
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const [form, setForm] = useState<HousingArrangement>({});
  const openEditor = () => {
    setError('');
    setForm(housing || {});
    setEditing(true);
  };

  if (!housing && !canEdit) return null;

  const lbl = dense
    ? 'text-[11px] font-bold uppercase tracking-widest text-slate-400'
    : 'text-caption font-bold text-slate-400 uppercase tracking-wider';
  const val = dense
    ? 'text-[11px] font-semibold text-slate-700 truncate'
    : 'text-sm font-semibold text-slate-800 mt-1 break-words';
  const inputCls = 'w-full px-3 py-2 text-xs font-medium border border-slate-200 rounded-xl bg-white outline-none focus:ring-1 focus:ring-brand-primary';
  const labelCls = 'text-caption font-semibold text-slate-500 mb-1 block';

  const rows: Array<[string, string | undefined]> = [
    ['Address', housing?.address],
    ['City', housing?.city],
    ['Country', housing?.country],
    ['Zip / Postal Code', housing?.zipCode],
    ['Rent', housing?.rentCost ? `${housing.rentCost}${housing.rentPeriod ? ` / ${housing.rentPeriod}` : ''}` : undefined],
    ['Contact Person / Landlord', housing?.landlordName],
    ['Landlord Phone', housing?.landlordPhone],
  ].filter(([, v]) => !!v) as Array<[string, string]>;

  const save = async () => {
    setBusy(true);
    setError('');
    const clean = (v?: string) => (typeof v === 'string' ? v.trim() : '');
    const housingPayload: HousingArrangement = {};
    if (clean(form.address)) housingPayload.address = clean(form.address);
    if (clean(form.city)) housingPayload.city = clean(form.city);
    if (clean(form.country)) housingPayload.country = clean(form.country);
    if (clean(form.zipCode)) housingPayload.zipCode = clean(form.zipCode);
    if (clean(form.rentCost)) housingPayload.rentCost = clean(form.rentCost);
    if (form.rentPeriod && HOUSING_RENT_PERIODS.includes(form.rentPeriod)) housingPayload.rentPeriod = form.rentPeriod;
    if (clean(form.landlordName)) housingPayload.landlordName = clean(form.landlordName);
    if (clean(form.landlordPhone)) housingPayload.landlordPhone = clean(form.landlordPhone);
    if (clean(form.otherDetails)) housingPayload.otherDetails = clean(form.otherDetails);
    try {
      const data = await safeFetch(`/api/applications/${applicationId}/placements/${entry.placementId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ scope: 'housing', housing: housingPayload }),
      });
      if (data?.placements) {
        onUpdated?.(normalizePlacements(data.placements));
      }
      setEditing(false);
    } catch (e: any) {
      setError(e?.message || 'Failed to save housing details.');
    } finally {
      setBusy(false);
    }
  };

  const readOnlyNote = !canEdit && housing
    ? (hint || 'Only team admins can update housing details.')
    : hint;

  return (
    <>
      <div className={`mt-4 rounded-xl border ${housing ? 'border-sky-100 bg-sky-50/60' : 'border-dashed border-slate-200 bg-slate-50/40'} p-3`}>
        <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1.5">
          <span className="inline-flex items-center gap-1.5">
            <Home className={`${dense ? 'h-3 w-3' : 'h-3.5 w-3.5'} text-sky-500`} />
            <span className={`${dense ? 'text-[10px]' : 'text-xs'} font-bold uppercase tracking-wider text-sky-900`}>Housing Arrangements</span>
          </span>
          {canEdit ? (
            <button
              type="button"
              onClick={openEditor}
              disabled={busy}
              className={`inline-flex cursor-pointer items-center gap-1 rounded-lg border font-bold transition-all disabled:cursor-not-allowed disabled:opacity-50 ${
                dense
                  ? 'border-sky-200 bg-white px-2 py-1 text-[10px] text-sky-700 hover:bg-sky-50'
                  : 'border-sky-200 bg-white px-3 py-1.5 text-xs text-sky-700 hover:bg-sky-50'
              }`}
            >
              {housing ? <Pencil className={`${dense ? 'h-2.5 w-2.5' : 'h-3.5 w-3.5'}`} /> : <Plus className={`${dense ? 'h-2.5 w-2.5' : 'h-3.5 w-3.5'}`} />}
              {housing ? 'Edit Housing' : 'Add Housing Details'}
            </button>
          ) : readOnlyNote ? (
            <span className={`${dense ? 'text-[11px]' : 'text-[10px]'} font-semibold text-slate-400`}>{readOnlyNote}</span>
          ) : null}
        </div>

        {housing ? (
          <div className="mt-2.5">
            {rows.length > 0 && (
              <div className={fieldGrid(dense, rows.length)}>
                {rows.map(([label, value]) => (
                  <div key={label} className={dense ? 'flex min-w-0 items-start gap-1.5' : ''}>
                    {dense ? (
                      <>
                        <MapPin className="mt-0.5 h-2.5 w-2.5 shrink-0 text-slate-300" />
                        <div className="min-w-0">
                          <p className={lbl}>{label}</p>
                          <p className={val}>{value}</p>
                        </div>
                      </>
                    ) : (
                      <>
                        <p className={lbl}>{label}</p>
                        <p className={val}>{value}</p>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
            {housing.otherDetails && (
              <div className={`${dense ? 'mt-2' : 'mt-3'} rounded-lg border border-sky-100 bg-white/70 px-2.5 py-2`}>
                <p className={lbl}>Other Details</p>
                <p className={`${dense ? 'mt-0.5 text-[11px]' : 'mt-1 text-xs'} whitespace-pre-wrap leading-relaxed text-slate-600`}>{housing.otherDetails}</p>
              </div>
            )}
            {housing.updatedByName && (
              <p className={`${dense ? 'mt-1.5 text-[11px]' : 'mt-2 text-[10px]'} font-semibold text-slate-400`}>
                Updated by {housing.updatedByName}
                {housing.updatedAt ? ` · ${new Date(housing.updatedAt).toLocaleDateString()}` : ''}
              </p>
            )}
          </div>
        ) : (
          <p className={`${dense ? 'mt-1.5 text-[10px]' : 'mt-2 text-xs'} font-medium text-slate-400`}>
            No housing arrangement recorded yet.
          </p>
        )}
      </div>

      {editing && (
        <Modal onClose={() => { if (!busy) setEditing(false); }}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl max-h-[92vh] overflow-y-auto mx-4 p-6 animate-fade-in" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between gap-3 mb-2">
              <h4 className="text-sm font-bold text-slate-900 flex items-center gap-2">
                <Home className="h-4 w-4 text-sky-500" />
                Housing Arrangements
              </h4>
              <button onClick={() => { if (!busy) setEditing(false); }} aria-label="Close" className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-all cursor-pointer">
                <X className="h-4 w-4" />
              </button>
            </div>
            <p className="text-xs text-slate-500 mb-4 leading-relaxed">
              Add the accommodation details for this placement — the address, rent and landlord, or anything else the team should know.
            </p>

            <div className="space-y-3">
              <div>
                <label className={labelCls}>Address</label>
                <input className={inputCls} value={form.address || ''} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Street address, unit, building" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className={labelCls}>City</label>
                  <input className={inputCls} value={form.city || ''} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="e.g. Orlando" />
                </div>
                <div>
                  <label className={labelCls}>Country</label>
                  <input className={inputCls} value={form.country || ''} onChange={(e) => setForm({ ...form, country: e.target.value })} placeholder="e.g. United States" />
                </div>
                <div>
                  <label className={labelCls}>Zip / Postal Code</label>
                  <input className={inputCls} value={form.zipCode || ''} onChange={(e) => setForm({ ...form, zipCode: e.target.value })} placeholder="e.g. 32801" />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>Rent cost (optional)</label>
                  <input className={inputCls} value={form.rentCost || ''} onChange={(e) => setForm({ ...form, rentCost: e.target.value })} placeholder="e.g. $450" />
                </div>
                <div>
                  <label className={labelCls}>Rent period</label>
                  <select className={inputCls} value={form.rentPeriod || ''} onChange={(e) => setForm({ ...form, rentPeriod: (e.target.value || undefined) as any })}>
                    <option value="">Select period…</option>
                    {HOUSING_RENT_PERIODS.map((p) => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>Contact person / Landlord (optional)</label>
                  <input className={inputCls} value={form.landlordName || ''} onChange={(e) => setForm({ ...form, landlordName: e.target.value })} placeholder="e.g. Sarah Mitchell" />
                </div>
                <div>
                  <label className={labelCls}>Landlord phone (optional)</label>
                  <input className={inputCls} value={form.landlordPhone || ''} onChange={(e) => setForm({ ...form, landlordPhone: e.target.value })} placeholder="e.g. +1 555 010 2233" />
                </div>
              </div>
              <div>
                <label className={labelCls}>Any other detail (optional)</label>
                <textarea rows={3} className={`${inputCls} resize-none`} value={form.otherDetails || ''} onChange={(e) => setForm({ ...form, otherDetails: e.target.value })} placeholder="Furnished? Shared or private? Anything the team should know…" />
              </div>

              {error && (
                <div className="rounded-lg bg-rose-50 border border-rose-200 px-3 py-2 text-xs font-semibold text-rose-600">{error}</div>
              )}

              <div className="flex items-center justify-end gap-2 pt-1">
                <button onClick={() => { if (!busy) setEditing(false); }}
                  className="px-4 py-2 rounded-xl border border-slate-200 text-xs font-bold text-slate-600 hover:bg-slate-50 transition-all cursor-pointer">
                  Cancel
                </button>
                <button onClick={save} disabled={busy}
                  className="px-4 py-2 rounded-xl bg-sky-600 text-xs font-bold text-white hover:bg-sky-700 transition-all cursor-pointer disabled:opacity-60 inline-flex items-center gap-1.5">
                  {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                  {busy ? 'Saving…' : 'Save Housing Details'}
                </button>
              </div>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}
